const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

async function parseJsonSafe(res) {
  return res.json().catch(() => ({}));
}

function buildAuthHeaders(token, extra = {}) {
  return {
    Authorization: `Bearer ${token}`,
    ...extra,
  };
}

/* =========================
   ADMIN
========================= */

export async function adminGetSubscriptionModels(token) {
  const res = await fetch(`${API_BASE}/api/admin/subscriptions/models`, {
    headers: buildAuthHeaders(token),
  });
  const data = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(data?.error || "Errore caricamento modelli abbonamento");
  }
  return data;
}

export async function adminGetSubscriptionPlans(token, { includeInactive = false } = {}) {
  const qs = new URLSearchParams();
  if (includeInactive) qs.set("include_inactive", "true");

  const res = await fetch(
    `${API_BASE}/api/admin/subscriptions/plans${qs.toString() ? `?${qs}` : ""}`,
    {
      headers: buildAuthHeaders(token),
    }
  );
  const data = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(data?.error || "Errore caricamento piani abbonamento");
  }
  return data;
}

export async function adminCreateSubscriptionPlan(payload, token) {
  const res = await fetch(`${API_BASE}/api/admin/subscriptions/plans`, {
    method: "POST",
    headers: buildAuthHeaders(token, {
      "Content-Type": "application/json",
    }),
    body: JSON.stringify(payload),
  });
  const data = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(data?.error || "Errore creazione piano abbonamento");
  }
  return data;
}

export async function adminUpdateSubscriptionPlan(planId, payload, token) {
  const res = await fetch(`${API_BASE}/api/admin/subscriptions/plans/${planId}`, {
    method: "PUT",
    headers: buildAuthHeaders(token, {
      "Content-Type": "application/json",
    }),
    body: JSON.stringify(payload),
  });
  const data = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(data?.error || "Errore aggiornamento piano abbonamento");
  }
  return data;
}

export async function adminListSubscriptionPlanImages(token) {
  const res = await fetch(`${API_BASE}/api/admin/subscriptions/plan-images`, {
    headers: buildAuthHeaders(token),
  });
  const data = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(data?.error || "Errore caricamento immagini decorative");
  }
  return data;
}

export async function adminUploadSubscriptionPlanImage(file, token) {
  const formData = new FormData();
  formData.append("image", file);

  const res = await fetch(`${API_BASE}/api/admin/subscriptions/plan-images`, {
    method: "POST",
    headers: buildAuthHeaders(token),
    body: formData,
  });
  const data = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(data?.error || "Errore caricamento immagine");
  }
  return data;
}

export async function adminGetSubscriptionRequests(token, { status } = {}) {
  const qs = new URLSearchParams();
  if (status) qs.set("status", status);

  const res = await fetch(
    `${API_BASE}/api/admin/subscriptions/requests${qs.toString() ? `?${qs}` : ""}`,
    {
      headers: buildAuthHeaders(token),
    }
  );
  const data = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(data?.error || "Errore caricamento richieste abbonamenti");
  }
  return data;
}

export async function adminReviewSubscriptionRequest(requestId, payload, token) {
  const res = await fetch(
    `${API_BASE}/api/admin/subscriptions/requests/${requestId}/review`,
    {
      method: "POST",
      headers: buildAuthHeaders(token, {
        "Content-Type": "application/json",
      }),
      body: JSON.stringify(payload),
    }
  );
  const data = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(data?.error || "Errore revisione richiesta abbonamento");
  }
  return data;
}

export async function adminGetSupplierSubscriptionPlanMatrix(token) {
  const res = await fetch(
    `${API_BASE}/api/admin/subscriptions/supplier-plans`,
    {
      headers: buildAuthHeaders(token),
    },
  );

  const data = await parseJsonSafe(res);

  if (!res.ok) {
    throw new Error(
      data?.error || "Errore caricamento associazioni fornitori",
    );
  }

  return data;
}

export async function adminAssignSubscriptionPlanToSupplier(payload, token) {
  const res = await fetch(
    `${API_BASE}/api/admin/subscriptions/supplier-plans`,
    {
      method: "POST",
      headers: buildAuthHeaders(token, {
        "Content-Type": "application/json",
      }),
      body: JSON.stringify(payload),
    },
  );

  const data = await parseJsonSafe(res);

  if (!res.ok) {
    throw new Error(
      data?.error || "Errore associazione piano al fornitore",
    );
  }

  return data;
}

export async function adminDeleteSupplierSubscriptionPlan(supplierPlanId, token) {
  const res = await fetch(
    `${API_BASE}/api/admin/subscriptions/supplier-plans/${supplierPlanId}`,
    {
      method: "DELETE",
      headers: buildAuthHeaders(token),
    },
  );

  const data = await parseJsonSafe(res);

  if (!res.ok) {
    throw new Error(
      data?.error || "Errore rimozione associazione piano",
    );
  }

  return data;
}

export async function adminGetUserSubscriptions(token, filters = {}) {
  const qs = new URLSearchParams();

  if (filters.status) qs.set("status", filters.status);
  if (filters.supplier_id) qs.set("supplier_id", String(filters.supplier_id));
  if (filters.plan_id) qs.set("plan_id", String(filters.plan_id));
  if (filters.q) qs.set("q", filters.q);
  if (filters.page) qs.set("page", String(filters.page));
  if (filters.page_size) qs.set("page_size", String(filters.page_size));

  const res = await fetch(
    `${API_BASE}/api/admin/subscriptions/user-subscriptions?${qs.toString()}`,
    {
      headers: buildAuthHeaders(token),
    },
  );

  const data = await parseJsonSafe(res);

  if (!res.ok) {
    throw new Error(
      data?.error || "Errore caricamento sottoscrizioni utenti",
    );
  }

  return data;
}

/* =========================
   FORNITORE
========================= */

export async function supplierGetSubscriptionPlans(token) {
  const res = await fetch(`${API_BASE}/api/fornitore/subscriptions/plans`, {
    headers: buildAuthHeaders(token),
  });
  const data = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(data?.error || "Errore caricamento piani disponibili");
  }
  return data;
}

export async function supplierRequestSubscriptionPlan(payload, token) {
  const res = await fetch(`${API_BASE}/api/fornitore/subscriptions/request`, {
    method: "POST",
    headers: buildAuthHeaders(token, {
      "Content-Type": "application/json",
    }),
    body: JSON.stringify(payload),
  });
  const data = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(data?.error || "Errore invio richiesta adesione");
  }
  return data;
}

export async function supplierGetMySubscriptions(token) {
  const res = await fetch(`${API_BASE}/api/fornitore/subscriptions/my`, {
    headers: buildAuthHeaders(token),
  });
  const data = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(data?.error || "Errore caricamento abbonamenti cliente del fornitore");
  }
  return data;
}

/* =========================
   CLIENTE
========================= */

export async function getSubscriptionPlansBySupplier(supplierId, token) {
  const qs = new URLSearchParams({ fornitore_id: String(supplierId) });

  const res = await fetch(`${API_BASE}/api/subscriptions/plans?${qs.toString()}`, {
    headers: buildAuthHeaders(token),
  });
  const data = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(data?.error || "Errore caricamento piani abbonamento");
  }
  return data;
}

export async function subscribeToPlan(payload, token) {
  const res = await fetch(`${API_BASE}/api/subscriptions/subscribe`, {
    method: "POST",
    headers: buildAuthHeaders(token, {
      "Content-Type": "application/json",
    }),
    body: JSON.stringify(payload),
  });
  const data = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(data?.error || "Errore attivazione abbonamento");
  }
  return data;
}

export async function getMySubscriptions(token) {
  const res = await fetch(`${API_BASE}/api/subscriptions/me`, {
    headers: buildAuthHeaders(token),
  });
  const data = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(data?.error || "Errore caricamento miei abbonamenti");
  }
  return data;
}

export async function cancelMySubscription(subscriptionId, token) {
  const res = await fetch(`${API_BASE}/api/subscriptions/${subscriptionId}/cancel`, {
    method: "POST",
    headers: buildAuthHeaders(token),
  });
  const data = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(data?.error || "Errore cancellazione abbonamento");
  }
  return data;
}

export async function getSubscriptionAvailableSuppliers(token) {
  const res = await fetch(`${API_BASE}/api/subscriptions/suppliers`, {
    headers: buildAuthHeaders(token),
  });

  const data = await parseJsonSafe(res);

  if (!res.ok) {
    throw new Error(data?.error || "Errore caricamento fornitori disponibili");
  }

  return data;
}

export async function getAvailableSubscriptionPlans(token) {
  const res = await fetch(`${API_BASE}/api/subscriptions/available-plans`, {
    headers: buildAuthHeaders(token),
  });

  const data = await parseJsonSafe(res);

  if (!res.ok) {
    throw new Error(data?.error || "Errore caricamento abbonamenti disponibili");
  }

  return data;
}
export async function getSubscriptionActivationDetails(planId, token) {
  const res = await fetch(`${API_BASE}/api/subscriptions/activation/${planId}`, {
    headers: buildAuthHeaders(token),
  });

  const data = await parseJsonSafe(res);

  if (!res.ok) {
    throw new Error(data?.error || "Errore caricamento attivazione abbonamento");
  }

  return data;
}

export async function createSubscriptionCheckoutSession(payload, token) {
  const res = await fetch(`${API_BASE}/api/subscriptions/stripe/checkout-session`, {
    method: "POST",
    headers: buildAuthHeaders(token, {
      "Content-Type": "application/json",
    }),
    body: JSON.stringify(payload),
  });

  const data = await parseJsonSafe(res);

  if (!res.ok) {
    throw new Error(data?.detail || data?.error || "Errore avvio pagamento abbonamento");
  }

  return data;
}
